import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../../utils/apiBase';

/**
 * Pre-engagement KID receipt. Kept separate from contract signing and
 * applicant progress; silent when no KID has yet been prepared.
 */
export default function WorkerKidCard() {
  const [kid, setKid] = useState(null);
  const [issue, setIssue] = useState('');
  const [confirmReceipt, setConfirmReceipt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const refresh = useCallback(async () => {
    const token = localStorage.getItem('workerToken');
    if (!token) return;
    try {
      const response = await axios.get(`${API_BASE}/worker/key-information-document`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 15000,
      });
      setKid(response.data?.kid || null);
      setIssue('');
    } catch (_err) {
      setIssue('Document status is temporarily unavailable.');
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const acknowledge = async () => {
    if (!confirmReceipt || submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('workerToken');
      const res = await axios.post(
        `${API_BASE}/worker/key-information-document/acknowledge`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 },
      );
      setKid(res.data?.kid || null);
      setIssue('');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setIssue(typeof detail === 'string' ? detail : detail?.message || 'Receipt could not be recorded. Please try again.');
    } finally { setSubmitting(false); }
  };

  if (!kid?.rendered_file_url && kid?.status !== 'outdated') return null;
  if (kid?.status === 'outdated') {
    return (
      <section className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="worker-kid-outdated">
        <h3 className="text-sm font-semibold text-amber-900">Updated Key Information Document required</h3>
        <p className="mt-1 text-xs text-amber-800">
          Your pay or engagement information changed. Ask your recruiter for a revised KID before agreeing terms.
        </p>
      </section>
    );
  }

  return (
    <section className="rounded-2xl border border-teal-200 bg-teal-50 p-4" data-testid="worker-early-kid">
      <h3 className="text-sm font-semibold text-teal-950">Your Key Information Document</h3>
      <p className="mt-1 text-sm text-teal-900">
        Review how your PAYE agency engagement and example take-home pay work before agreeing employment terms.
        This is not your employment contract.
      </p>
      <a href={kid.rendered_file_url} target="_blank" rel="noopener noreferrer"
        className="mt-3 inline-block rounded-lg bg-white px-3 py-2 text-sm font-medium text-teal-800 underline">
        View Key Information Document (PDF)
      </a>
      {kid.verified ? (
        <p className="mt-3 text-sm font-medium text-green-800">Receipt acknowledged. This does not mean your recruitment checks are approved.</p>
      ) : (
        <div className="mt-3 space-y-3">
          <label className="flex items-start gap-2 text-sm text-teal-900">
            <input type="checkbox" checked={confirmReceipt} onChange={(e) => setConfirmReceipt(e.target.checked)} />
            <span>I confirm I have received the Key Information Document made available above.</span>
          </label>
          <button type="button" disabled={!confirmReceipt || submitting} onClick={acknowledge}
            className="rounded-lg bg-teal-800 px-3 py-2 text-sm font-medium text-white disabled:opacity-50">
            {submitting ? 'Saving receipt…' : 'Acknowledge receipt'}
          </button>
        </div>
      )}
      {issue && <p role="alert" className="mt-2 text-xs text-red-700">{issue}</p>}
    </section>
  );
}
